import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    CSVCalendarFields,
    ICalendarItemRow,
    ICSVCalendarItemRow,
    IExcludedCalendar,
    ODataValue,
} from '@models';
import { MapperService } from '@services';
import {
    CSVParser,
    ICSVParseError,
    ICSVParseErrorCode,
    ICSVParseErrorType,
    ICSVParseResult,
} from '@services/csv-parser.service';
import { SUPPORTED_DATE_FORMATS } from '@services/generics/constants';
import { ODataService } from '@services/generics/odata.service';

import * as moment from 'moment';
import {
    Observable,
    of,
    throwError,
} from 'rxjs';
import {
    map,
    switchMap,
} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CalendarService extends ODataService<IExcludedCalendar, number> {
    protected _endpoint = '/odata/Calendars';

    constructor(
        http: HttpClient,
        private _parser: CSVParser<CSVCalendarFields>,
        private _appState: AppStateFacade,
    ) {
        super(http, MapperService.excludedCalendarMapper);
    }

    public getDefaultCalendar(): Observable<IExcludedCalendar> {
        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetDefaultCalendar()`;
        return this._http.get(endpoint)
            .pipe(
                map(response => this._mapData(response, MapperService.excludedCalendarMapper)),
            ) as Observable<IExcludedCalendar>;
    }

    public updateAndMap(id: number, data: IExcludedCalendar): Observable<IExcludedCalendar> {
        return this.update(id, data)
            .pipe(
                map(response => this._mapData(response, MapperService.excludedCalendarMapper)),
            ) as Observable<IExcludedCalendar>;
    }

    public isCalendarNameValid(calendarName: string): Observable<boolean> {
        if (!calendarName) {
            return throwError(null);
        }

        return this._http.post<ODataValue<boolean>>(
            `${this._endpoint}/UiPath.Server.Configuration.OData.CalendarExists`, { calendarName })
            .pipe(
                switchMap(response =>
                    response.value ? throwError(null) : of(true),
                ),
            );
    }

    public parseFile(file: File): Observable<ICSVParseResult<CSVCalendarFields>> {
        return this._parser.parseFile(
            file,
            {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: true,
                maxFileSize: 128 * 1024,
            },
        ).pipe(
            map(({ data, errors, meta }: ICSVParseResult<CSVCalendarFields>) => ({
                meta,
                // this._rowMapper mutates the errors argument.
                data: data.map((row: ICSVCalendarItemRow, i: number) => this._rowMapper(row, i, errors)),
                // sort and switch to 1-indexed rows.
                errors: errors.map(err => ({ ...err, row: err.row + 1 })).sort((err1, err2) => err1.row - err2.row),
            })),
        );
    }

    private _parseDate(row: number, errors: ICSVParseError<CSVCalendarFields>[], field: CSVCalendarFields, dateString?: string) {
        if (!dateString) {
            errors.push({
                type: ICSVParseErrorType.FieldMismatch,
                code: ICSVParseErrorCode.InvalidData,
                row,
                field,
            });
            return;
        }

        const parsedDate = moment.tz(
            dateString,
            SUPPORTED_DATE_FORMATS,
            true,
            this._appState.settings.get(Setting.timezone)!,
        );

        if (parsedDate.isValid()) {
            return parsedDate;
        }

        errors.push({
            type: ICSVParseErrorType.FieldError,
            code: ICSVParseErrorCode.InvalidData,
            row,
            field,
        });
    }

    private _rowMapper(row: ICSVCalendarItemRow, index: number, errors: ICSVParseError<CSVCalendarFields>[]): ICalendarItemRow {
        return {
            excludedDate: this._parseDate(index, errors, CSVCalendarFields.ExcludedDate, row.ExcludedDate),
        };
    }
}
